import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { CheckCircle2, AlertTriangle, ExternalLink, Wallet as WalletIcon, X, ChevronRight, ChevronDown, CalendarPlus, SearchX, XCircle, Star, SlidersHorizontal, LifeBuoy } from 'lucide-react';
import { getAcademy, getAcademyTraining, getAcademyTrainingen, downloadAcademyAgenda, annuleerAcademyBoeking } from '../services/medewerkerApi';
import type { AcademyBoeking, AcademySubcategorie, AcademyTraining, Wallet } from '../services/medewerkerApi';
import { AcademyRij } from '../components/academy/AcademyRij';
import { AcademyTegel } from '../components/academy/AcademyTegel';
import { AcademyDetailModal } from '../components/academy/AcademyDetailModal';
import { TrainingReviewModal } from '../components/TrainingReviewModal';
import { KlachtenSectie, type KlachtVoorinvulling } from '../components/klachten/KlachtenSectie';
import { AcademyZoekbalk } from '../components/academy/AcademyZoekbalk';
import { AcademyFilterBalk, FilterGroep } from '../components/academy/AcademyFilterBalk';
import { ACCENT_KLEUR, datumLang, eur } from '../components/academy/academyStijl';
import type { RijAccent } from '../components/academy/academyStijl';
import {
  LEGE_FILTERS, binnenReistijd, heeftFilters, laadPostcodeTabel, leesFilters, leesPostcode, naarParams, schrijfFilters, wkrGrendelVast,
} from '../components/academy/academyFilters';
import type { AcademyFilters, PostcodeTabel } from '../components/academy/academyFilters';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

function datumTijd(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** Rijkleur per swiper (streepje naast de titel); populair heeft geen vaste kleur. */
const RIJ_ACCENT: Record<string, RijAccent | undefined> = {
  mentaal: 'mentaal',
  fysiek: 'fysiek',
  professioneel: 'professioneel',
  passie: 'passie',
  financieel: 'financieel',
};

const PotBalk: React.FC<{ label: string; resterend: number; totaal: number; kleur: string }> = ({ label, resterend, totaal, kleur }) => {
  const pct = totaal > 0 ? Math.min(100, Math.round((resterend / totaal) * 100)) : 0;
  return (
    <div className="min-w-[9rem] flex-1">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="text-gray-500">{label}</span>
        <span className="font-semibold text-gray-900">{eur.format(resterend)}</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={label}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: kleur }} />
      </div>
    </div>
  );
};

/** Compacte, lichte wallet-strook bovenaan de catalogus. */
const WalletStrook: React.FC<{ wallet: Wallet }> = ({ wallet }) => {
  const { t } = useTranslation('medewerker');
  const totaal = wallet.budget_totaal ?? 0;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl border border-gray-200 bg-white px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="rounded-lg bg-emerald-50 p-2 text-emerald-600">
          <WalletIcon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-sm font-semibold text-gray-900">{t('academy.walletTitel', { defaultValue: 'Jouw ontwikkelbudget {{jaar}}', jaar: wallet.boekjaar })}</p>
          <p className="text-xs text-gray-500">{t('academy.kortingUitleg', { defaultValue: '{{pct}}% inkoopkorting wordt automatisch toegepast.', pct: wallet.korting_pct || 5 })}</p>
        </div>
      </div>
      {!wallet.actief ? (
        <p className="flex-1 text-sm text-gray-600">
          {t('academy.nietActiefUitleg', {
            defaultValue: 'Je werkgever heeft de Academy nog niet geactiveerd. Je kunt alvast rondkijken; boeken kan zodra het budget is vrijgegeven.',
          })}
        </p>
      ) : wallet.model === 'on_demand' ? (
        <p className="text-sm font-medium text-emerald-700">🟢 {t('academy.onbeperkt', { defaultValue: 'Onbeperkt budget (on demand)' })}</p>
      ) : wallet.potten ? (
        <div className="flex flex-1 flex-wrap gap-4">
          <PotBalk label={t('academy.potPro', { defaultValue: 'Professioneel' })} resterend={wallet.potten.pro.resterend} totaal={wallet.potten.pro.totaal} kleur={ACCENT_KLEUR.professioneel} />
          <PotBalk label={t('academy.potHobby', { defaultValue: 'Persoonlijk (hobby)' })} resterend={wallet.potten.hobby.resterend} totaal={wallet.potten.hobby.totaal} kleur={ACCENT_KLEUR.passie} />
        </div>
      ) : (
        <div className="flex flex-1 flex-wrap gap-4">
          <PotBalk label={t('academy.beschikbaar', { defaultValue: 'Beschikbaar' })} resterend={wallet.budget_resterend ?? 0} totaal={totaal} kleur={ACCENT_KLEUR.professioneel} />
          {wallet.hobby_resterend !== null && wallet.hobby_resterend !== undefined && wallet.hobby_cap_bedrag !== null && (
            <PotBalk label={t('academy.hobbyRuimte', { defaultValue: 'Hobby-ruimte (WKR)' })} resterend={wallet.hobby_resterend} totaal={wallet.hobby_cap_bedrag} kleur={ACCENT_KLEUR.passie} />
          )}
        </div>
      )}
    </div>
  );
};

const BoekingStatus: React.FC<{ boeking: AcademyBoeking }> = ({ boeking }) => {
  const { t } = useTranslation('medewerker');
  const config = {
    wacht_op_betaling: { cls: 'bg-amber-50 text-amber-700 border-amber-300', label: t('academy.statusWacht', { defaultValue: 'Wacht op betaling' }) },
    bevestigd: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-300', label: t('academy.statusBevestigd', { defaultValue: 'Bevestigd' }) },
    geannuleerd: { cls: 'bg-gray-100 text-gray-500 border-gray-200', label: t('academy.statusGeannuleerd', { defaultValue: 'Geannuleerd' }) },
  }[boeking.status];
  // Annulering binnen 48 u: status blijft 'bevestigd' (bedrag verschuldigd), maar de plek is vrijgegeven.
  if (boeking.geannuleerd_op && boeking.status !== 'geannuleerd') {
    return (
      <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
        {t('academy.statusGeannuleerdBescherming', { defaultValue: 'Geannuleerd · bedrag verschuldigd' })}
      </span>
    );
  }
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${config.cls}`}>{config.label}</span>;
};

const Melding: React.FC<{ soort: 'goed' | 'let_op'; onSluit: () => void; children: React.ReactNode }> = ({ soort, onSluit, children }) => {
  const { t } = useTranslation('medewerker');
  const Icon = soort === 'goed' ? CheckCircle2 : AlertTriangle;
  return (
    <div
      className={`flex items-start gap-2 rounded-xl border p-3 text-sm ${
        soort === 'goed' ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-amber-300 bg-amber-50 text-amber-800'
      }`}
      role="status"
    >
      <Icon className={`h-5 w-5 shrink-0 ${soort === 'goed' ? 'text-emerald-600' : 'text-amber-600'}`} aria-hidden="true" />
      <span className="flex-1">{children}</span>
      <button type="button" onClick={onSluit} aria-label={t('algemeen.sluiten', { defaultValue: 'Sluiten' })}>
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

/**
 * Pijler 2: Belagom Academy — Netflix-portaal. Lichte interface met filter-sidebar links, de 4 vaste
 * categorie-rijen (swipers), donkerblauwe cinemategels en het Informatie-scherm
 * (detail-pop-up) om te activeren uit de wallet (+ iDEAL-bijbetaling).
 */
export const MedewerkerAcademyPage: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const hoofdgroep = searchParams.get('hoofdgroep');
  const subcategorie = searchParams.get('subcategorie');
  const betaling = searchParams.get('betaling');
  const alles = searchParams.get('alles') === '1';
  // Discovery Engine: alle filters in de URL (deelbaar); de reistijd (km) blijft client-side.
  const filters = useMemo(() => leesFilters(searchParams), [searchParams]);
  const filtersKey = JSON.stringify(schrijfFilters(filters));

  const [zoek, setZoek] = useState('');
  const [zoekDebounced, setZoekDebounced] = useState('');
  const [open, setOpen] = useState<AcademyTraining | null>(null);
  const [reviewBoeking, setReviewBoeking] = useState<AcademyBoeking | null>(null);
  // Helpdesk & transactiefouten (klachtenreglement, categorie POP-budget transacties)
  const [klachtVoorinvulling, setKlachtVoorinvulling] = useState<KlachtVoorinvulling | undefined>();
  const naarHelpdesk = () => document.getElementById('helpdesk')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const meldProbleem = (b: AcademyBoeking) => {
    setKlachtVoorinvulling({ sleutel: Date.now(), referentie: b.id, categorie: 'ACADEMY_POP_TRANSACTION' });
    requestAnimationFrame(naarHelpdesk);
  };
  // Doorlink vanuit de footer (?helpdesk=1)
  const helpdeskParam = searchParams.get('helpdesk') === '1';
  useEffect(() => {
    if (helpdeskParam) requestAnimationFrame(naarHelpdesk);
  }, [helpdeskParam]);
  const [postcode, setPostcode] = useState<string | null>(() => leesPostcode());
  const [postcodeTabel, setPostcodeTabel] = useState<PostcodeTabel | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setZoekDebounced(zoek.trim()), 350);
    return () => clearTimeout(timer);
  }, [zoek]);

  // Postcode-centroïden pas laden als de Reistijd-slider echt gebruikt wordt (aparte chunk).
  useEffect(() => {
    if (filters.km !== null && postcode && !postcodeTabel) laadPostcodeTabel().then(setPostcodeTabel).catch(() => setPostcodeTabel({}));
  }, [filters.km, postcode, postcodeTabel]);

  // Heractivatie: nieuwe startdata van opleiders verschijnen vanzelf weer in de rijen.
  const academyQuery = useQuery({ queryKey: ['medewerker-academy'], queryFn: getAcademy, refetchInterval: 60_000 });
  const filterActief = alles || Boolean(hoofdgroep || subcategorie || zoekDebounced) || heeftFilters(filters);
  const trainingenQuery = useQuery({
    queryKey: ['medewerker-academy-trainingen', hoofdgroep, subcategorie, zoekDebounced, filtersKey],
    queryFn: () => getAcademyTrainingen(naarParams(filters, { hoofdgroep, subcategorie, zoek: zoekDebounced || null })),
    enabled: filterActief,
    // Instant-Fade: oude tegels blijven (gedimd) staan tot het nieuwe grid er is.
    placeholderData: keepPreviousData,
  });

  // Terug van Stripe Checkout: verse boekingen + wallet ophalen.
  useEffect(() => {
    if (betaling) queryClient.invalidateQueries({ queryKey: ['medewerker-academy'] });
  }, [betaling, queryClient]);

  const agendaMutation = useMutation({ mutationFn: (b: AcademyBoeking) => downloadAcademyAgenda(b.id, b.titel) });
  const [annuleerMelding, setAnnuleerMelding] = useState<string | null>(null);
  const annuleerMutation = useMutation({
    mutationFn: (b: AcademyBoeking) => annuleerAcademyBoeking(b.id),
    onSuccess: (r) => {
      setAnnuleerMelding(r.message);
      queryClient.invalidateQueries({ queryKey: ['medewerker-academy'] });
    },
    onError: (e) => setAnnuleerMelding(foutMelding(e, t('academy.annulerenMislukt', { defaultValue: 'Annuleren is niet gelukt.' }))),
  });
  const annuleer = (b: AcademyBoeking) => {
    const binnen48 = b.sessie_start ? new Date(b.sessie_start).getTime() - Date.now() < 48 * 3_600_000 : false;
    const vraag = binnen48 && b.status === 'bevestigd'
      ? t('academy.annulerenBevestigBescherming', { defaultValue: 'De training start binnen 48 uur. Annuleren kan, maar het bedrag blijft verschuldigd (annuleringsbescherming). Doorgaan?' })
      : t('academy.annulerenBevestig', { defaultValue: 'Weet je zeker dat je deze boeking wilt annuleren? Tot 48 uur voor de start is dat kosteloos.' });
    if (window.confirm(vraag)) annuleerMutation.mutate(b);
  };

  const wallet = academyQuery.data?.wallet;
  const advies = academyQuery.data?.advies ?? null;
  const wkrVast = wkrGrendelVast(wallet);
  const hoofdgroepen = useMemo(() => academyQuery.data?.catalogus.hoofdgroepen ?? [], [academyQuery.data?.catalogus.hoofdgroepen]);
  const subcategorieen = useMemo(() => academyQuery.data?.catalogus.subcategorieen ?? [], [academyQuery.data?.catalogus.subcategorieen]);
  const rijen = academyQuery.data?.catalogus.rijen ?? [];
  const boekingen = academyQuery.data?.boekingen ?? [];

  // Functionele updater: react-router v7 verwerkt navigaties in een transition, dus een
  // kopie van `searchParams` uit de render-closure kan al verouderd zijn (bijv. na een
  // suggestie-klik gevolgd door een debounce-timer).
  const setParams = (wijzigingen: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, waarde] of Object.entries(wijzigingen)) {
        if (waarde) next.set(key, waarde);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  };
  const setParam = (key: string, waarde: string | null) => setParams({ [key]: waarde });
  const setFilters = (f: AcademyFilters) => setParams({ ...schrijfFilters(f), alles: null });

  // Suggestie-dropdown: cursus → Informatie-scherm, opleider → profiel, thema → pill.
  const kiesCursus = useCallback(
    (id: number) => {
      getAcademyTraining(id)
        .then((detail) => setOpen(detail))
        .catch(() => undefined);
    },
    []
  );
  const kiesOpleider = useCallback((id: number) => navigate(`/medewerker/academy/opleider/${id}`), [navigate]);
  const kiesThema = (hg: string, slug: string | null) => {
    setZoek('');
    setParams({ hoofdgroep: hg, subcategorie: slug, alles: null });
  };

  // Reistijd-filter (Postcode-Grendel): 100% lokaal, e-learnings blijven altijd zichtbaar.
  const resultaten = useMemo(() => {
    const lijst = trainingenQuery.data ?? [];
    if (filters.km === null || !postcode) return lijst;
    return lijst.filter((tr) => binnenReistijd(tr, postcodeTabel, postcode, filters.km));
  }, [trainingenQuery.data, filters.km, postcode, postcodeTabel]);
  const verborgenDoorAfstand = (trainingenQuery.data?.length ?? 0) - resultaten.length;

  const actieveHoofdgroep = useMemo(() => hoofdgroepen.find((c) => c.key === hoofdgroep) ?? null, [hoofdgroepen, hoofdgroep]);
  const actieveSub = useMemo(() => subcategorieen.find((s) => s.slug === subcategorie) ?? null, [subcategorieen, subcategorie]);

  /** Pills: alleen thema's met trainingen (binnen de gekozen map); zolang de catalogus leeg is, alle thema's. */
  const pills = useMemo(() => {
    const basis = hoofdgroep ? subcategorieen.filter((s) => s.hoofdgroep === hoofdgroep) : subcategorieen;
    const gevuld = basis.filter((s) => s.aantal > 0);
    return gevuld.length > 0 ? gevuld : basis;
  }, [subcategorieen, hoofdgroep]);

  const kiesHoofdgroep = (key: string) => setParams({ hoofdgroep: hoofdgroep === key ? null : key, subcategorie: null, alles: null });
  const kiesPill = (s: AcademySubcategorie) =>
    setParams(subcategorie === s.slug ? { subcategorie: null } : { subcategorie: s.slug, hoofdgroep: s.hoofdgroep, alles: null });
  const wisAlles = () => {
    setZoek('');
    setParams({ hoofdgroep: null, subcategorie: null, alles: null, ...schrijfFilters(LEGE_FILTERS) });
  };

  const resultTitel = actieveSub
    ? `${actieveSub.emoji ?? ''} ${actieveSub.naam}`.trim()
    : actieveHoofdgroep
      ? `${actieveHoofdgroep.emoji} ${actieveHoofdgroep.label}`
      : zoekDebounced
        ? t('academy.resultatenVoor', { defaultValue: 'Resultaten voor "{{zoek}}"', zoek: zoekDebounced })
        : t('academy.alleTrainingen', { defaultValue: 'Alle trainingen' });

  // Actieve filterbadges (bovenaan de workspace) — elk klikbaar om los te wissen.
  const tags: Array<{ key: string; label: string; wis: () => void }> = [];
  if (zoekDebounced) tags.push({ key: 'zoek', label: `“${zoekDebounced}”`, wis: () => setZoek('') });
  if (actieveHoofdgroep) tags.push({ key: 'hg', label: `${actieveHoofdgroep.emoji} ${actieveHoofdgroep.label}`, wis: () => setParams({ hoofdgroep: null, subcategorie: null }) });
  if (actieveSub) tags.push({ key: 'sub', label: `${actieveSub.emoji ?? ''} ${actieveSub.naam}`.trim(), wis: () => setParam('subcategorie', null) });
  for (const v of filters.vorm) {
    tags.push({ key: `vorm-${v}`, label: t(`academy.leervorm.${v}`, { defaultValue: { e_learning: 'E-Learning', workshop: 'Workshop', opleiding: 'Opleiding' }[v] }), wis: () => setFilters({ ...filters, vorm: filters.vorm.filter((x) => x !== v) }) });
  }
  for (const sh of filters.shift) {
    tags.push({ key: `shift-${sh}`, label: t(`academy.filter.shift.${sh}`, { defaultValue: { dag: 'Dag', avond: 'Avond', nacht: 'Nacht / On-Demand' }[sh] }), wis: () => setFilters({ ...filters, shift: filters.shift.filter((x) => x !== sh) }) });
  }
  if (filters.dagen.length) {
    const dagen = filters.dagen.map((d) => t(`academy.filter.dag${d}`, { defaultValue: ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'][d - 1] })).join(', ');
    tags.push({ key: 'dagen', label: dagen, wis: () => setFilters({ ...filters, dagen: [] }) });
  }
  if (filters.budget) tags.push({ key: 'budget', label: t('academy.filter.budget', { defaultValue: 'Alleen passend binnen mijn budget' }), wis: () => setFilters({ ...filters, budget: false }) });
  if (filters.score) tags.push({ key: 'score', label: `⭐ ${filters.score}+`, wis: () => setFilters({ ...filters, score: null }) });
  if (filters.wkr) tags.push({ key: 'wkr', label: t('academy.filter.wkr', { defaultValue: "Verberg WKR-risico (Hobby's)" }), wis: () => setFilters({ ...filters, wkr: false }) });
  if (filters.advies) tags.push({ key: 'advies', label: t('academy.filter.adviesTraject', { defaultValue: 'Mijn Advies-Traject' }), wis: () => setFilters({ ...filters, advies: false }) });
  if (filters.km !== null) tags.push({ key: 'km', label: t('academy.filter.maxKm', { defaultValue: 'Max. {{km}} km', km: filters.km }), wis: () => setFilters({ ...filters, km: null }) });
  if (alles) tags.push({ key: 'alles', label: t('academy.alleTrainingen', { defaultValue: 'Alle trainingen' }), wis: () => setParam('alles', null) });
  const aantalActief = tags.length + (filters.sort !== 'relevantie' ? 1 : 0);

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">{t('academy.eyebrow', { defaultValue: 'Medewerkersportaal' })}</p>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">{t('pijler.academy', { defaultValue: 'BeLagom Academy' })}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('academy.subtitel', { defaultValue: 'Trainingen en ontwikkeling, betaald uit jouw budget.' })}</p>
      </header>

      {betaling === 'gelukt' && (
        <Melding soort="goed" onSluit={() => setParam('betaling', null)}>
          {t('academy.betalingGelukt', { defaultValue: 'Je betaling is gelukt. Je boeking is bevestigd.' })}
        </Melding>
      )}
      {betaling === 'geannuleerd' && (
        <Melding soort="let_op" onSluit={() => setParam('betaling', null)}>
          {t('academy.betalingGeannuleerd', { defaultValue: 'De betaling is geannuleerd. Je kunt de betaling afronden via "Mijn boekingen".' })}
        </Melding>
      )}

      {academyQuery.isError && (
        <p className="text-sm text-red-600" role="alert">
          {foutMelding(academyQuery.error, t('academy.laadfout', { defaultValue: 'De Academy kon niet worden geladen.' }))}
        </p>
      )}
      {wallet && <WalletStrook wallet={wallet} />}

      <div className="lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] lg:gap-8">
        {/* Linker sidebar: zoeken, sorteren, hoofdgroepen/thema's en alle filters verticaal onder elkaar */}
        <aside className="mb-6 lg:sticky lg:top-4 lg:mb-0 lg:max-h-[calc(100vh-2rem)] lg:self-start lg:overflow-y-auto">
          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={filtersOpen}
            aria-controls="academy-filterpaneel"
            className="flex w-full items-center justify-between gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-900 lg:hidden"
          >
            <span className="inline-flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-gray-500" aria-hidden="true" />
              {aantalActief > 0
                ? t('academy.filter.mobielActief', { defaultValue: 'Filters ({{n}} actief)', n: aantalActief })
                : t('academy.filter.mobiel', { defaultValue: 'Zoeken & filters' })}
            </span>
            <ChevronDown className={`h-4 w-4 text-gray-500 transition ${filtersOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
          <div id="academy-filterpaneel" className={`${filtersOpen ? 'mt-3 block' : 'hidden'} lg:mt-0 lg:block`}>
            <AcademyFilterBalk
              filters={filters}
              onChange={setFilters}
              wallet={wallet}
              advies={advies}
              postcode={postcode}
              onPostcode={setPostcode}
              zoekveld={<AcademyZoekbalk waarde={zoek} onChange={setZoek} onKiesCursus={kiesCursus} onKiesOpleider={kiesOpleider} onKiesThema={kiesThema} />}
            >
              <FilterGroep titel={t('academy.hoofdgroepen', { defaultValue: 'Hoofdgroepen' })}>
                <nav aria-label={t('academy.hoofdgroepen', { defaultValue: 'Hoofdgroepen' })} className="flex flex-col gap-1">
                  {hoofdgroepen.map((c) => {
                    const actief = hoofdgroep === c.key;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => kiesHoofdgroep(c.key)}
                        aria-pressed={actief}
                        className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm font-medium transition ${
                          actief ? 'bg-[#0B1F3A] text-white' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span aria-hidden="true">{c.emoji}</span>
                        <span className="min-w-0 flex-1 truncate">{c.label}</span>
                        <span className={`text-xs ${actief ? 'text-slate-300' : 'text-gray-400'}`}>{c.aantal}</span>
                      </button>
                    );
                  })}
                </nav>
                {(hoofdgroep || subcategorie) && pills.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5 border-l-2 border-gray-100 pl-2.5">
                    {pills.map((s) => {
                      const actief = subcategorie === s.slug;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => kiesPill(s)}
                          aria-pressed={actief}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                            actief ? 'border-sky-600 bg-sky-50 text-sky-800' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400'
                          }`}
                        >
                          {s.emoji && <span aria-hidden="true">{s.emoji}</span>}
                          {s.naam}
                          {s.aantal > 0 && <span className="text-gray-400">{s.aantal}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </FilterGroep>
            </AcademyFilterBalk>
          </div>
        </aside>

        <div className="min-w-0 space-y-6">
          {/* Actieve filterbadges bovenaan de workspace */}
          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" aria-label={t('academy.filter.actief', { defaultValue: 'Actieve filters' })}>
              {tags.map((tag) => (
                <button
                  key={tag.key}
                  type="button"
                  onClick={tag.wis}
                  className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-800 hover:bg-sky-100"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                  {tag.label}
                </button>
              ))}
              <button type="button" onClick={wisAlles} className="ml-1 text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline">
                {t('academy.filter.wisAlles', { defaultValue: 'Alle filters wissen' })}
              </button>
            </div>
          )}

          {filterActief ? (
            /* Gefilterde resultaten (zoekterm, map of pill) — hier blijven ook volle cursussen zichtbaar */
            <section className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-lg font-bold text-gray-900">
                  {resultTitel}
                  {trainingenQuery.data && <span className="font-normal text-gray-400"> ({resultaten.length})</span>}
                </h2>
                <button type="button" onClick={wisAlles} className="text-sm font-medium text-sky-700 hover:text-sky-800">
                  {t('academy.wisFilters', { defaultValue: 'Alles tonen' })}
                </button>
              </div>
              {trainingenQuery.isLoading && <p className="text-sm text-gray-500">…</p>}
              {trainingenQuery.isError && (
                <p className="text-sm text-red-600" role="alert">
                  {foutMelding(trainingenQuery.error, t('academy.trainingenFout', { defaultValue: 'Trainingen konden niet worden geladen.' }))}
                </p>
              )}
              {trainingenQuery.isSuccess && resultaten.length === 0 && (
                /* Empty State: onmogelijke combinatie */
                <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center">
                  <SearchX className="mx-auto h-10 w-10 text-gray-300" aria-hidden="true" />
                  <p className="mt-3 text-base font-semibold text-gray-900">
                    {t('academy.geenResultaten', { defaultValue: 'Geen cursussen gevonden binnen deze combinatie.' })}
                  </p>
                  <p className="mx-auto mt-1.5 max-w-lg text-sm text-gray-500">
                    {filters.km !== null || verborgenDoorAfstand > 0
                      ? t('academy.geenResultatenTipAfstand', {
                          defaultValue: "Tip: Probeer de Reistijd-slider te vergroten of switch naar de optie 'Nacht / On-Demand' voor flexibele e-learnings.",
                        })
                      : t('academy.geenResultatenTip', { defaultValue: 'Tip: Zet een filter uit, probeer een ander trefwoord of kies een thema in het filtermenu.' })}
                  </p>
                  <button type="button" onClick={wisAlles} className="mt-4 rounded-full bg-[#0B1F3A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1A3459]">
                    {t('academy.wisFilters', { defaultValue: 'Alles tonen' })}
                  </button>
                </div>
              )}
              {verborgenDoorAfstand > 0 && resultaten.length > 0 && (
                <p className="text-xs text-gray-500">
                  {t('academy.verborgenDoorAfstand', { defaultValue: '{{n}} cursus(sen) buiten je reistijd verborgen.', n: verborgenDoorAfstand })}
                </p>
              )}
              {/* Instant-Fade: tijdens het herladen vervagen de oude tegels (opacity .2, geen klikken) */}
              <div
                className={`grid grid-cols-1 gap-4 transition-opacity duration-300 sm:grid-cols-2 xl:grid-cols-3 ${
                  trainingenQuery.isFetching && trainingenQuery.isPlaceholderData ? 'pointer-events-none opacity-20' : 'opacity-100'
                }`}
                aria-busy={trainingenQuery.isFetching}
              >
                {resultaten.map((tr) => (
                  <AcademyTegel key={tr.id} training={tr} onOpen={setOpen} className="w-full" />
                ))}
              </div>
            </section>
          ) : (
            /* De categorie-rijen (swipers) */
            <div className="space-y-8">
              {academyQuery.isLoading && (
                <div className="flex gap-4 overflow-hidden" aria-hidden="true">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="aspect-[16/12] w-[20rem] shrink-0 animate-pulse rounded-xl bg-white ring-1 ring-gray-200" />
                  ))}
                </div>
              )}
              {academyQuery.isSuccess && rijen.length === 0 && (
                <div className="rounded-2xl border border-gray-200 bg-white p-5">
                  <p className="text-sm text-gray-600">{t('academy.catalogusLeeg', { defaultValue: 'De catalogus wordt nog gevuld. Kom binnenkort terug!' })}</p>
                </div>
              )}
              {rijen.map((rij) => {
                const accent = RIJ_ACCENT[rij.key];
                // WKR-filter: Rij 4 (Persoonlijke Passie & Hobby's) verdwijnt volledig
                if (rij.key === 'passie' && (filters.wkr || wkrVast)) return null;
                return (
                  <AcademyRij
                    key={rij.key}
                    id={rij.key}
                    titel={rij.titel}
                    subtitel={rij.subtitel}
                    emoji={rij.emoji}
                    accent={accent ? ACCENT_KLEUR[accent] : undefined}
                    trainingen={rij.trainingen}
                    onOpen={setOpen}
                  />
                );
              })}
              <button
                type="button"
                onClick={() => setParam('alles', '1')}
                className="inline-flex w-full items-center justify-center gap-1 rounded-xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700 hover:border-gray-400"
              >
                {t('academy.alleTrainingenBekijken', { defaultValue: 'Alle trainingen van A tot Z bekijken' })}
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}

          <section>
            <h2 className="mb-3 text-lg font-bold text-gray-900">{t('academy.mijnBoekingen', { defaultValue: 'Mijn boekingen' })}</h2>
            {boekingen.length === 0 ? (
              <p className="rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-500">
                {t('academy.geenBoekingen', { defaultValue: 'Je hebt nog niets geboekt.' })}
              </p>
            ) : (
              <ul className="grid gap-3 md:grid-cols-2">
                {boekingen.map((b) => (
                  <li key={b.id} className="rounded-2xl border border-gray-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900">{b.titel}</p>
                        <p className="text-xs text-gray-500">
                          {b.categorie_label}
                          {b.sessie_start
                            ? ` · ${t('academy.startOp', { defaultValue: 'start {{datum}}', datum: datumLang(b.sessie_start) })}${
                                b.aantal_lessen > 1 ? ` · ${t('academy.aantalLessen', { defaultValue: '{{n}} lessen', n: b.aantal_lessen })}` : ''
                              }`
                            : (b.datum_tijd || b.wanneer) && ` · ${b.datum_tijd ? datumTijd(b.datum_tijd) : b.wanneer}`}
                        </p>
                      </div>
                      <BoekingStatus boeking={b} />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
                      <span>
                        {t('academy.netto', { defaultValue: 'Netto' })}: <strong className="text-gray-900">{eur.format(b.netto_bedrag)}</strong>
                      </span>
                      <span>
                        {t('academy.uitBudget', { defaultValue: 'Uit je budget' })}: {eur.format(b.wallet_deel)}
                      </span>
                      {b.bijbetaling > 0 && (
                        <span>
                          {t('academy.bijbetaling', { defaultValue: 'Bijbetaling' })}: {eur.format(b.bijbetaling)}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {b.status === 'wacht_op_betaling' && b.stripe_checkout_url && (
                        <a href={b.stripe_checkout_url} className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 underline">
                          {t('academy.betalingAfronden', { defaultValue: 'Betaling afronden' })}
                          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                      )}
                      {!b.geannuleerd_op && b.status !== 'geannuleerd' && (!b.sessie_start || new Date(b.sessie_start).getTime() > Date.now()) && (
                        <button
                          type="button"
                          onClick={() => annuleer(b)}
                          disabled={annuleerMutation.isPending}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-red-600 disabled:opacity-50"
                        >
                          <XCircle className="h-4 w-4" aria-hidden="true" />
                          {t('academy.annuleren', { defaultValue: 'Annuleren' })}
                        </button>
                      )}
                      {b.heeft_agenda && !b.geannuleerd_op && (
                        <button
                          type="button"
                          onClick={() => agendaMutation.mutate(b)}
                          disabled={agendaMutation.isPending}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 hover:text-sky-800 disabled:opacity-50"
                        >
                          <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                          {t('academy.inAgenda', { defaultValue: 'Zet in mijn agenda (.ics)' })}
                        </button>
                      )}
                      {b.kan_reviewen && (
                        <button
                          type="button"
                          onClick={() => setReviewBoeking(b)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:text-amber-800"
                        >
                          <Star className="h-4 w-4" aria-hidden="true" />
                          {t('academy.beoordelen', { defaultValue: 'Beoordeel deze cursus' })}
                        </button>
                      )}
                      {b.heeft_review && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700">
                          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                          {t('academy.beoordeeld', { defaultValue: 'Beoordeeld' })}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => meldProbleem(b)}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800"
                      >
                        <LifeBuoy className="h-4 w-4" aria-hidden="true" />
                        {t('academy.probleemMelden', { defaultValue: 'Probleem melden' })}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {annuleerMelding && (
              <p className={`mt-2 text-sm ${annuleerMutation.isError ? 'text-red-600' : 'text-emerald-700'}`} role="status">
                {annuleerMelding}
              </p>
            )}
            {agendaMutation.isError && (
              <p className="mt-2 text-sm text-red-600" role="alert">
                {t('academy.detail.agendaFout', { defaultValue: 'Het agenda-bestand kon niet worden gedownload.' })}
              </p>
            )}
          </section>

          <section id="helpdesk" className="scroll-mt-20">
            <h2 className="mb-1 flex items-center gap-2 text-lg font-bold text-gray-900">
              <LifeBuoy className="h-5 w-5 text-gray-500" aria-hidden="true" />
              {t('academy.helpdeskTitel', { defaultValue: 'Helpdesk & transactiefouten' })}
            </h2>
            <p className="mb-4 text-sm text-gray-500">
              {t('academy.helpdeskIntro', {
                defaultValue: 'Ging er iets mis met een bestelling, je ontwikkelbudget of de toegang tot een training? Meld het hier bij BeLagom. Je werkgever ziet deze melding niet.',
              })}
            </p>
            <KlachtenSectie voorinvulling={klachtVoorinvulling} />
          </section>

        </div>
      </div>

      {open && <AcademyDetailModal training={open} wallet={wallet} onClose={() => setOpen(null)} />}
      {reviewBoeking && (
        <TrainingReviewModal trainingId={reviewBoeking.training_id} trainingTitel={reviewBoeking.titel} onClose={() => setReviewBoeking(null)} />
      )}
    </div>
  );
};

export default MedewerkerAcademyPage;
